import { useState, useEffect } from "react";
import { Mail, Phone, Calendar, MapPin, Users, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function BookingsList() {
  const [bookings, setBookings] = useState([]);

  const load = () => api.get("/admin/bookings").then((res) => setBookings(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch {
      toast.error("Could not delete booking");
    }
  };

  return (
    <div data-testid="admin-bookings">
      <div className="mb-6">
        <h2 className="font-serif-display text-3xl text-sc-espresso">Event Bookings</h2>
        <p className="text-sm text-sc-espresso/60 mt-1">Requests submitted through the booking form.</p>
      </div>

      {bookings.length === 0 ? (
        <p className="text-sc-espresso/50">No booking requests yet.</p>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl p-6 border border-sc-gold/15" data-testid={`booking-${b.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-serif-display text-xl text-sc-espresso">{b.name}</h3>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-sc-espresso/50">{new Date(b.created_at).toLocaleString()}</span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        className="grid place-items-center w-8 h-8 rounded-full text-sc-bordeaux hover:bg-sc-bordeaux/10 transition-colors"
                        data-testid={`delete-booking-${b.id}`}
                        aria-label="Delete booking"
                      >
                        <Trash2 size={16} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="delete-booking-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this booking?</AlertDialogTitle>
                        <AlertDialogDescription>
                          The request from <strong>{b.name}</strong> ({b.email}) will be permanently removed. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancel-delete-booking">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-sc-bordeaux hover:bg-sc-bordeaux/90"
                          onClick={() => remove(b.id)}
                          data-testid={`confirm-delete-booking-${b.id}`}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-sc-espresso/80">
                <span className="flex items-center gap-2"><Mail size={14} className="text-sc-gold" />{b.email}</span>
                {b.phone && <span className="flex items-center gap-2"><Phone size={14} className="text-sc-gold" />{b.phone}</span>}
                {b.event_date && <span className="flex items-center gap-2"><Calendar size={14} className="text-sc-gold" />{b.event_date}</span>}
                {b.event_location && <span className="flex items-center gap-2"><MapPin size={14} className="text-sc-gold" />{b.event_location}</span>}
                {b.num_guests && <span className="flex items-center gap-2"><Users size={14} className="text-sc-gold" />{b.num_guests} guests</span>}
              </div>
              {b.message && <p className="mt-3 text-sm text-sc-espresso/70 italic border-l-2 border-sc-gold/40 pl-3">{b.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
